import type { Locale } from "@shared/i18n";
import type { MarketReport } from "@shared/lib/report";

export const demoReport: MarketReport = {
  report_name: "LumenDesk — exemple illustratif",
  offer_name: "LumenDesk",
  analyzed_url: "https://example.com/lumendesk",
  priority_segment:
    "Sociétés de services multi-sites qui centralisent encore leurs demandes internes par e-mail",
  coverage: "Démonstration fictive conçue uniquement pour présenter l’interface",
  created_on: "2026-07-13",
  executive_summary:
    "Deux organisations fictives présentent des changements opérationnels compatibles avec une plateforme de gestion des demandes. L’angle le plus pertinent consiste à mesurer le délai de traitement sur une équipe pilote.",
  research_strategy: {
    offer_hypothesis:
      "LumenDesk aide les sociétés multi-sites à centraliser les demandes internes dispersées entre e-mails et tableurs.",
    selected_signals: [
      {
        family: "Opérations manuelles",
        rationale: "Les flux dispersés constituent le processus directement remplacé par l’offre.",
        explored_sources: ["Offres d’emploi", "Témoignages métier", "Documentation"],
      },
      {
        family: "Croissance et expansion",
        rationale: "L’ajout de sites augmente le coût de la coordination et ouvre une fenêtre de changement.",
        explored_sources: ["Actualités d’entreprise", "Pages carrière"],
      },
      {
        family: "Changement d’organisation",
        rationale: "Un nouveau responsable opérationnel peut porter la standardisation du processus.",
        explored_sources: ["Annonces d’entreprise", "Profils professionnels"],
      },
      {
        family: "Technologies et intégrations",
        rationale: "La présence d’outils fragmentés permet d’évaluer l’effort réel d’adoption.",
        explored_sources: ["Offres d’emploi", "Pages d’intégrations"],
      },
    ],
    adaptation_note:
      "Exemple fictif : la piste conformité a été écartée, sans relation directe avec la promesse de l’offre.",
  },
  audience: {
    decision_maker: "Responsable des opérations ou directeur des systèmes internes",
    desired_progress: "Réduire les demandes perdues et rendre les délais de traitement mesurables",
    favorable_event: "Ouverture d’un site, réorganisation d’équipe ou remplacement d’un outil partagé",
    exclusions: [
      "Organisation mono-site de moins de dix personnes",
      "Processus déjà centralisé dans une plateforme équivalente",
      "Absence de responsable opérationnel identifié",
    ],
  },
  opportunities: [
    {
      organization: "Nordal Services",
      category: "Entreprise de maintenance multi-sites — exemple fictif",
      readiness: "Besoin explicite",
      priority: 87,
      market_clue:
        "Une publication fictive décrit la création d’une fonction dédiée à la coordination des demandes entre six agences.",
      source_summary:
        "Le scénario de démonstration mentionne des demandes reçues par plusieurs boîtes e-mail et un objectif de réduction des délais.",
      relevance:
        "LumenDesk centralise ce type de flux sans imposer de changement immédiat aux équipes terrain.",
      momentum: "La nouvelle fonction crée une fenêtre naturelle pour structurer le processus.",
      source_name: "Source fictive — démonstration Nordal",
      source_link: "https://example.com/demo/nordal",
      source_format: "Exemple illustratif",
      observed_on: "Date fictive",
      contact_route: "Formulaire professionnel de l’entreprise",
      message_draft:
        "Bonjour, votre nouvelle organisation semble viser une meilleure coordination des demandes entre agences. LumenDesk permet de centraliser les flux reçus par e-mail et de mesurer les délais sans bouleverser les habitudes terrain. Serait-il utile de comparer le fonctionnement actuel avec un pilote limité à une agence ?",
      verification_note: "Cas fictif : ne pas utiliser ce message pour une prise de contact réelle.",
      assessment: {
        need_intensity: 4,
        solution_alignment: 5,
        decision_window: 4,
        contact_path: 4,
        source_confidence: 4,
      },
    },
    {
      organization: "Groupe Helio",
      category: "Réseau de conseil régional — exemple fictif",
      readiness: "Fenêtre favorable",
      priority: 76,
      market_clue:
        "Le scénario fictif annonce l’ouverture de deux bureaux et l’harmonisation des outils internes.",
      source_summary:
        "La page de démonstration associe l’expansion à un chantier de standardisation des demandes administratives.",
      relevance:
        "Le déploiement progressif de LumenDesk correspond à une organisation qui souhaite harmoniser plusieurs sites.",
      momentum: "Les nouveaux bureaux permettent de tester un processus commun avant sa généralisation.",
      source_name: "Source fictive — démonstration Helio",
      source_link: "https://example.com/demo/helio",
      source_format: "Exemple illustratif",
      observed_on: "Date fictive",
      contact_route: "Page entreprise ou formulaire professionnel",
      message_draft:
        "Bonjour, l’ouverture de nouveaux bureaux est souvent le moment où les demandes internes deviennent difficiles à suivre entre sites. LumenDesk propose un espace commun avec des délais visibles par équipe. Un test sur l’un des nouveaux bureaux vous aiderait-il à évaluer ce fonctionnement avant harmonisation ?",
      verification_note: "Cas fictif : aucune organisation réelle n’est visée.",
      assessment: {
        need_intensity: 3,
        solution_alignment: 4,
        decision_window: 4,
        contact_path: 4,
        source_confidence: 4,
      },
    },
  ],
  market_patterns: [
    {
      label: "Expansion et standardisation",
      occurrences: 2,
      interpretation:
        "Dans ce scénario fictif, l’ajout de sites rend la circulation des demandes plus difficile à piloter.",
    },
    {
      label: "Mesure des délais",
      occurrences: 2,
      interpretation:
        "La valeur perçue repose davantage sur la visibilité opérationnelle que sur le simple remplacement de l’e-mail.",
    },
  ],
  action_plan: {
    positioning: "Présenter un pilote limité qui mesure les délais avant et après centralisation.",
    launch: "Choisir une équipe et documenter son flux actuel sur une semaine type.",
    follow_through: "Configurer un seul formulaire et suivre trois indicateurs simples.",
    target_outcome: "Décider de la généralisation à partir du délai, du volume perdu et de l’adoption.",
  },
  caveats: [
    "Toutes les organisations, observations et sources de cette démonstration sont fictives.",
    "Une analyse réelle doit vérifier chaque page d’origine avant toute action.",
    "Une opportunité documentée ne constitue jamais une intention d’achat.",
  ],
};

/**
 * The same fictional report in English. `readiness` keeps the stored values
 * of the report schema (French enum): the interface labels them.
 */
const demoReportEn: MarketReport = {
  report_name: "LumenDesk — illustrative example",
  offer_name: "LumenDesk",
  analyzed_url: "https://example.com/lumendesk",
  priority_segment: "Multi-site service companies that still handle internal requests by email",
  coverage: "Fictional demo built only to show the interface",
  created_on: "2026-07-13",
  executive_summary:
    "Two fictional organizations show operational changes that fit a request management platform. The most relevant angle is to measure turnaround time with a pilot team.",
  research_strategy: {
    offer_hypothesis:
      "LumenDesk helps multi-site companies centralize internal requests scattered across emails and spreadsheets.",
    selected_signals: [
      {
        family: "Manual operations",
        rationale: "Scattered workflows are the process the offer directly replaces.",
        explored_sources: ["Job postings", "Industry testimonials", "Documentation"],
      },
      {
        family: "Growth and expansion",
        rationale: "Adding sites raises the cost of coordination and opens a window for change.",
        explored_sources: ["Company news", "Careers pages"],
      },
      {
        family: "Organizational change",
        rationale: "A new operations lead can drive the standardization of the process.",
        explored_sources: ["Company announcements", "Professional profiles"],
      },
      {
        family: "Technologies and integrations",
        rationale: "Fragmented tools help gauge the actual adoption effort.",
        explored_sources: ["Job postings", "Integrations pages"],
      },
    ],
    adaptation_note: "Fictional example: the compliance angle was set aside, as it has no direct link to the offer’s promise.",
  },
  audience: {
    decision_maker: "Head of operations or director of internal systems",
    desired_progress: "Lose fewer requests and make turnaround times measurable",
    favorable_event: "A new site opening, a team reorganization or the replacement of a shared tool",
    exclusions: [
      "Single-site organization with fewer than ten people",
      "Process already centralized in an equivalent platform",
      "No identified operations lead",
    ],
  },
  opportunities: [
    {
      organization: "Nordal Services",
      category: "Multi-site maintenance company — fictional example",
      readiness: "Besoin explicite",
      priority: 87,
      market_clue: "A fictional post describes a new role dedicated to coordinating requests across six branches.",
      source_summary:
        "The demo scenario mentions requests received in several email inboxes and a goal of reducing turnaround times.",
      relevance: "LumenDesk centralizes this kind of flow without forcing field teams to change overnight.",
      momentum: "The new role creates a natural window to structure the process.",
      source_name: "Fictional source — Nordal demo",
      source_link: "https://example.com/demo/nordal",
      source_format: "Illustrative example",
      observed_on: "Fictional date",
      contact_route: "The company’s business contact form",
      message_draft:
        "Hello, your new setup seems aimed at better coordinating requests across branches. LumenDesk centralizes the requests you receive by email and measures turnaround times without disrupting how field teams work. Would it help to compare your current process with a pilot limited to one branch?",
      verification_note: "Fictional case: don’t use this message for real outreach.",
      assessment: {
        need_intensity: 4,
        solution_alignment: 5,
        decision_window: 4,
        contact_path: 4,
        source_confidence: 4,
      },
    },
    {
      organization: "Groupe Helio",
      category: "Regional consulting network — fictional example",
      readiness: "Fenêtre favorable",
      priority: 76,
      market_clue: "The fictional scenario announces two new offices and the harmonization of internal tools.",
      source_summary: "The demo page links the expansion to a project to standardize administrative requests.",
      relevance: "LumenDesk’s gradual rollout suits an organization that wants to align several sites.",
      momentum: "The new offices make it possible to test a shared process before rolling it out everywhere.",
      source_name: "Fictional source — Helio demo",
      source_link: "https://example.com/demo/helio",
      source_format: "Illustrative example",
      observed_on: "Fictional date",
      contact_route: "Company page or business contact form",
      message_draft:
        "Hello, opening new offices is often when internal requests become hard to track across sites. LumenDesk offers a shared space with turnaround times visible for each team. Would a trial in one of the new offices help you assess this way of working before rolling it out?",
      verification_note: "Fictional case: no real organization is targeted.",
      assessment: {
        need_intensity: 3,
        solution_alignment: 4,
        decision_window: 4,
        contact_path: 4,
        source_confidence: 4,
      },
    },
  ],
  market_patterns: [
    {
      label: "Expansion and standardization",
      occurrences: 2,
      interpretation: "In this fictional scenario, adding sites makes the flow of requests harder to manage.",
    },
    {
      label: "Measuring turnaround times",
      occurrences: 2,
      interpretation: "The perceived value rests more on operational visibility than on simply replacing email.",
    },
  ],
  action_plan: {
    positioning: "Pitch a limited pilot that measures turnaround times before and after centralization.",
    launch: "Pick one team and document its current workflow over a typical week.",
    follow_through: "Set up a single form and track three simple metrics.",
    target_outcome: "Decide on a wider rollout based on turnaround time, lost volume and adoption.",
  },
  caveats: [
    "Every organization, observation and source in this demo is fictional.",
    "A real analysis must check every original page before taking any action.",
    "A documented opportunity is never an intent to buy.",
  ],
};

/** The demo report in the account's language (the watch card, before the first pass). */
export function demoReportFor(locale: Locale): MarketReport {
  return locale === "en" ? demoReportEn : demoReport;
}
